import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MailX, UserPlus } from "lucide-react";

import {
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  H3,
  Input,
  Text,
} from "@lagoon/ui";

import { api, ApiError, type AllowlistEntry } from "../api.js";
import { Notice } from "./feedback.js";
import { describeBounce } from "./activity.js";

/**
 * The allowlist.
 *
 * Removing someone takes effect on their very next request — the gate re-checks
 * membership on every request rather than trusting what was true when their
 * session was issued. Worth saying in the UI, because it is the product promise.
 */
export function Allowlist(props: { siteId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const entries = useQuery({
    queryKey: ["allowlist", props.siteId],
    queryFn: async () => (await api.listAllowlist(props.siteId)).entries,
  });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ["allowlist", props.siteId] });

  const add = useMutation({
    mutationFn: () => api.addToAllowlist(props.siteId, email.trim()),
    onSuccess: async () => {
      setEmail("");
      setError(null);
      await invalidate();
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : "Could not add that address.");
    },
  });

  /**
   * Removal, which has to say when it did not happen (LGN-128).
   *
   * A refused remove used to be indistinguishable from a successful one: the
   * row stayed, the button came back, and nothing said why. On this path that
   * silence reads as "I took their access away" while the address is still on
   * the list, so the failure lands in the same notice the add path uses.
   */
  const remove = useMutation({
    mutationFn: (entryId: string) => api.removeFromAllowlist(props.siteId, entryId),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not remove that address. They still have access.",
      );
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    add.mutate();
  }

  const removing = remove.isPending;
  const removeEntry = remove.mutate;
  const columns = useMemo(() => allowlistColumns(removeEntry, removing), [removeEntry, removing]);

  return (
    <section className="lg:flex lg:flex-col lg:gap-4">
      <div>
        <H3>Who can see this</H3>
        <Text size="sm" tone="muted" className="lg:mt-1">
          Only these addresses can sign in. Removing someone blocks their very next request.
        </Text>
      </div>

      {error === null ? null : <Notice tone="danger">{error}</Notice>}

      <form className="lg:flex lg:flex-wrap lg:gap-3" onSubmit={submit}>
        <Input
          className="lg:min-w-56 lg:flex-1"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          placeholder="person@example.com"
          required
          aria-label="Email address to allow"
        />
        <Button variant="outline" type="submit" disabled={add.isPending || !email.trim()}>
          {add.isPending ? "Adding…" : "Add"}
        </Button>
      </form>

      <DataTable
        caption="Addresses allowed on this site"
        columns={columns}
        data={entries.data ?? []}
        loading={entries.isPending}
        loadingRows={3}
        empty={
          <EmptyState
            glow={false}
            icon={<UserPlus />}
            title="Nobody yet"
            description="Until you add someone, nobody can open this site."
          />
        }
      />

      {entries.data?.some((entry) => entry.bouncing) ? (
        <Text size="caption" tone="subtle" className="lg:flex lg:items-start lg:gap-2">
          <MailX aria-hidden="true" className="lg:mt-0.5 lg:size-4 lg:shrink-0" />
          <span>
            A bouncing address is still on the list, but the provider has told us mail to it does
            not arrive, so we have stopped sending it passcodes. Ask that person for another
            address.
          </span>
        </Text>
      ) : null}
    </section>
  );
}

/**
 * The columns, built at module scope.
 *
 * A `cell` renderer is a function that returns JSX, which is indistinguishable
 * from a component to a linter reading the file; defining them inside the
 * screen trips `react/no-unstable-nested-components` and, more to the point,
 * rebuilds the column definitions on every render. Taking what they close over
 * as arguments keeps both problems away.
 */
function allowlistColumns(
  removeEntry: (entryId: string) => void,
  removing: boolean,
): DataTableColumn<AllowlistEntry>[] {
  return [
    {
      accessorKey: "email",
      header: "Address",
      cell: (info) => {
        const entry = info.row.original;
        return (
          <span className="lg:flex lg:flex-wrap lg:items-center lg:gap-2">
            {entry.email}
            {entry.bouncing ? (
              <Badge tone="warning" title={describeBounce(entry.bounceReason)}>
                email bouncing
              </Badge>
            ) : null}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: (info) => (
        <span className="lg:flex lg:justify-end">
          <Button
            variant="ghost"
            size="sm"
            disabled={removing}
            onClick={() => {
              removeEntry(info.row.original.id);
            }}
          >
            Remove
          </Button>
        </span>
      ),
    },
  ];
}
